import { z } from 'zod'
import { publicOrganizerGroupSchema } from './organizer-group'

export const channelCategoryValues = [
  'sports',
  'music',
  'community',
  'news',
  'business',
  'other',
] as const

export const channelCategorySchema = z.enum(channelCategoryValues)

export type ChannelCategory = z.infer<typeof channelCategorySchema>

export const channelHandleSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{3,30}$/)

export const organizerKindValues = ['organizer', 'venue'] as const
export type OrganizerKind = (typeof organizerKindValues)[number]

const profileLinks = {
  website: z
    .string()
    .trim()
    .url()
    .max(300)
    .refine((url) => /^https?:\/\//i.test(url))
    .nullable()
    .optional(),
  instagram: z
    .string()
    .trim()
    .regex(/^@?[A-Za-z0-9._]{1,30}$/)
    .transform((handle) => handle.replace(/^@/, ''))
    .nullable()
    .optional(),
  coverUrl: z
    .string()
    .url()
    .refine((url) => /^https:\/\//i.test(url))
    .nullable()
    .optional(),
  kind: z.enum(organizerKindValues).optional(),
}

/** Public-facing summary of an announcement channel plus the viewer's subscription state. */
export const channelSummarySchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  handle: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** The organizer's own English description; optional so an older backend parses. */
  descriptionEn: z.string().nullable().optional(),
  avatarUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
  website: z.string().nullable(),
  instagram: z.string().nullable(),
  kind: z.enum(organizerKindValues),
  category: channelCategorySchema,
  isVerified: z.boolean(),
  isOfficial: z.boolean(),
  subscriberCount: z.number().int(),
  subscribed: z.boolean(),
  /** People can become members; optional so an older backend parses. */
  membersEnabled: z.boolean().optional(),
  /** The page is public; optional so an older backend parses. */
  isPublished: z.boolean().optional(),
  createdAt: z.string(),
})

export type ChannelSummary = z.infer<typeof channelSummarySchema>

export const channelPostSchema = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  body: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
})

export type ChannelPost = z.infer<typeof channelPostSchema>

export const channelDetailSchema = channelSummarySchema.extend({
  posts: z.array(channelPostSchema),
})

export type ChannelDetail = z.infer<typeof channelDetailSchema>

export const channelListResponseSchema = z.object({
  channels: z.array(channelSummarySchema),
})

export type ChannelListResponse = z.infer<typeof channelListResponseSchema>

export const channelListQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  subscribed: z.enum(['1']).optional(),
})

export const createChannelRequestSchema = z.object({
  businessId: z.string().uuid(),
  handle: channelHandleSchema,
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  descriptionEn: z.string().trim().max(500).optional(),
  category: channelCategorySchema.default('other'),
  avatarUrl: z.string().url().nullable().optional(),
  /** False keeps the new page a draft only the business sees; public when left out. */
  published: z.boolean().optional(),
  ...profileLinks,
})

export type CreateChannelRequest = z.infer<typeof createChannelRequestSchema>

export const updateChannelRequestSchema = z.object({
  handle: channelHandleSchema.optional(),
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  descriptionEn: z.string().trim().max(500).nullable().optional(),
  category: channelCategorySchema.optional(),
  avatarUrl: z.string().url().nullable().optional(),
  ...profileLinks,
})

export type UpdateChannelRequest = z.infer<typeof updateChannelRequestSchema>

export const setChannelPublishedRequestSchema = z.object({
  published: z.boolean(),
})

export type SetChannelPublishedRequest = z.infer<typeof setChannelPublishedRequestSchema>

export const createChannelPostRequestSchema = z.object({
  message: z.string().trim().min(1).max(2000),
})

export type CreateChannelPostRequest = z.infer<typeof createChannelPostRequestSchema>

export const channelResponseSchema = z.object({
  channel: channelSummarySchema,
})

export type ChannelResponse = z.infer<typeof channelResponseSchema>

export const channelDetailResponseSchema = z.object({
  channel: channelDetailSchema,
})

export type ChannelDetailResponse = z.infer<typeof channelDetailResponseSchema>

export const businessChannelResponseSchema = z.object({
  channel: channelSummarySchema.nullable(),
})

export type BusinessChannelResponse = z.infer<typeof businessChannelResponseSchema>

export const manageChannelQuerySchema = z.object({
  businessId: z.string().uuid(),
  /** One of the business's organizer pages; omitted, the oldest one. */
  channelId: z.string().uuid().optional(),
})

export const manageChannelResponseSchema = z.object({
  channel: channelDetailSchema.nullable(),
})

export type ManageChannelResponse = z.infer<typeof manageChannelResponseSchema>

/** One of an organization's profiles: an organizer page or a place's profile. */
export const organizationProfileSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(['organizer', 'venue', 'place']),
  handle: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** The place of a place profile; null for an organizer page. */
  listingId: z.string().uuid().nullable(),
  /** The profile is public: its place is live, or its organizer page is published. */
  isPublished: z.boolean(),
})

/** An organization's profiles, and whether profiles are switched on for the dashboard. */
export const organizationProfilesResponseSchema = z.object({
  enabled: z.boolean(),
  profiles: z.array(organizationProfileSchema),
})

export type OrganizationProfile = z.infer<typeof organizationProfileSchema>
export type OrganizationProfilesResponse = z.infer<typeof organizationProfilesResponseSchema>

export const channelPostResponseSchema = z.object({
  post: channelPostSchema,
})

export type ChannelPostResponse = z.infer<typeof channelPostResponseSchema>

/** One upcoming event on an organizer page. */
export const organizerEventSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  imageUrl: z.string().nullable(),
  venueName: z.string().nullable(),
  city: z.string().nullable(),
})

export type OrganizerEvent = z.infer<typeof organizerEventSchema>

/** An organizer or venue page as anyone sees it: the profile, what is coming up and what was. */
export const organizerProfileSchema = z.object({
  id: z.string().uuid(),
  handle: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** The organizer's own English description; optional so an older backend parses. */
  descriptionEn: z.string().nullable().optional(),
  avatarUrl: z.string().nullable(),
  coverUrl: z.string().nullable(),
  website: z.string().nullable(),
  instagram: z.string().nullable(),
  kind: z.enum(organizerKindValues),
  isVerified: z.boolean(),
  followerCount: z.number().int(),
  /** People can ask to become members ("Mitglied werden"). */
  membersEnabled: z.boolean().optional(),
  /** Active members; never who they are. */
  memberCount: z.number().int().optional(),
  events: z.array(organizerEventSchema),
  /** The latest ten past events, newest first; optional so an older backend parses. */
  pastEvents: z.array(organizerEventSchema).optional(),
  /** Group chats anyone can join from this page; optional so an older backend parses. */
  groups: z.array(publicOrganizerGroupSchema).optional(),
})

export type OrganizerProfile = z.infer<typeof organizerProfileSchema>

export const organizerForBusinessQuerySchema = z.object({
  /** The place the event happens at; its own organization is never named as a separate host. */
  listingId: z.string().uuid().optional(),
})

/** GET /api/public/organizers/by-business/{businessId}: the organizer page, else the plain host name. */
export const organizerForBusinessResponseSchema = z.object({
  organizer: z
    .object({
      id: z.string().uuid(),
      handle: z.string(),
      name: z.string(),
      description: z.string().nullable(),
      descriptionEn: z.string().nullable().optional(),
      avatarUrl: z.string().nullable(),
      coverUrl: z.string().nullable(),
      isVerified: z.boolean(),
      followerCount: z.number().int(),
      subscribed: z.boolean(),
    })
    .nullable(),
  /** Set only without an organizer page, and only when the business is not the place itself. */
  host: z.object({ name: z.string() }).nullable(),
})

export type OrganizerForBusinessResponse = z.infer<typeof organizerForBusinessResponseSchema>
